import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ThemeService } from '@aurrica-web/core-data';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faMoon, faSun } from '@fortawesome/free-solid-svg-icons';

/**
 * A toggle button that interacts with the `ThemeService` to switch between
 * light and dark modes.
 *
 * It utilizes high-contrast Tailwind classes:
 * - Light: Slate/Stone grayscale
 * - Dark: Deep navy/black with light text (#1a202e)
 *
 * @example
 * ```html
 * <lib-ui-theme-button />
 *
 **/
@Component({
  selector: 'lib-ui-theme-button',
  imports: [CommonModule, FontAwesomeModule],
  template: `<button
    type="button"
    class="inline-flex size-8 cursor-pointer items-center justify-center rounded-full mx-1 p-1 bg-stone-200 text-slate-950 transition-colors duration-200 hover:bg-slate-100 hover:text-gray-900 dark:bg-[#1a202e] dark:text-slate-50 dark:hover:bg-slate-800 dark:hover:text-white"
    (click)="themeService.toggleDarkMode()"
    [title]="
      themeService.isDarkMode() ? 'Switch to Light Mode' : 'Switch to Dark Mode'
    "
  >
    <fa-icon
      [icon]="themeService.isDarkMode() ? sunIcon : moonIcon"
      size="sm"
    ></fa-icon>
  </button>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThemeButton {
  protected readonly themeService = inject(ThemeService);

  protected readonly sunIcon = faSun;
  protected readonly moonIcon = faMoon;
}
